<script lang="ts">
import Vue, { CreateElement, VNode } from 'vue'

// Типы
interface DeviceChild {
  id?: string
  cls?: string
  name?: string
  template?: string
  x?: number
  y?: number
  width?: number
  height?: number
  orientation?: number
  side?: string
  mount?: string
  inverse_mount_side?: string
  slot_size?: number
  is_broken?: boolean
  is_used?: boolean
  children?: DeviceChild[]
  orient?: string
  [key: string]: any
}

interface DeviceData extends DeviceChild {
  $id?: string
  mount?: string
  [key: string]: any
}

interface SelectedDevice {
  $id?: string
  [key: string]: any
}

interface StyleObject {
  width?: string
  height?: string
  position?: string
  top?: string
  left?: string
  backgroundImage?: string
  backgroundSize?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'DEVICE',

  props: {
    data: {
      type: Object as () => DeviceData,
      default: () => ({})
    },
    side: {
      type: String,
      default: () => 'front'
    },
    position: {
      type: [String, Number] as Vue.PropType<string | number>,
      default: ''
    },
    editable: {
      type: Boolean,
      default: () => true
    },
    fit: {
      type: Boolean,
      default: () => false
    },
    x: {
      type: Number,
      default: 64
    },
    y: {
      type: Number,
      default: 64
    },
  },

  data(): {
    scale: number
    heap: Record<string, any>
    parent: StyleObject
    model: DeviceChild[]
  } {
    return {
      scale: 1,
      heap: {},
      parent: {
        width: '100%',
        height: '100%',
        position: 'relative',
      },
      model: [],
    }
  },

  computed: {
    getModel(): DeviceChild[] {
      return this.model
    },

    getSelected(): SelectedDevice | null {
      return this.$store.state.devices.activeDevice
    },

    getPosition(): string {
      return this.position
    },
  },

  watch: {
    data: {
      handler(newVal: DeviceData): void {
        if (newVal.$id) {
          this.setup()
        }
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  updated(): void {
    this.setup()
  },

  methods: {
    setup(): void {
      if (!this.data || !this.data.children) return
      this.model = this.parseData(this.data.children)
    },

    deviceClickHandler(e: MouseEvent): void {
      if (!e) return
      
      if (this.getSelected && (this.data.cls + '$' + this.data.id === this.getSelected.$id)) {
        this.$emit('select', null)
        return
      }
      
      if (this.data?.mount && this.data.mount !== this.side && this.getPosition) {
        this.$emit('select', this.getPosition)
        return
      }
      
      this.$emit('select', this.data)
    },

    getBackground(data: DeviceChild): string | undefined {
      if (data.template === undefined) return undefined
      
      let side: string | undefined
      
      if (data.cls === 'device') {
        side = this.getMount(data as DeviceData) === this.side ? 'front' : 'rear'
      } else {
        const side$ = this.checkSide(data)
        if (side$ === this.side) {
          side = 'image'
        }
      }
      
      const $id = `${data.cls}$${data.id}`
      if (side) {
        const path = this.$bridge.getImageLink($id, side)
        return path.replace(/\s/g, '%20')
      }
      return undefined
    },

    checkSide(d: DeviceChild): string | undefined {
      const side$ = d.side
      if (this.getMount(this.data) === 'front') {
        return side$
      } else {
        return side$ === 'front' ? 'rear' : 'front'
      }
    },

    mapChildren(item: DeviceChild): DeviceChild {
      const parent$ = item
      
      item.children?.forEach(child => {
        if (!child.width) {
          child.x = item.x
          child.y = item.y
          
          if (item.orientation == 1 || item.orientation == 3) {
            child.height = item.width
            child.width = item.height
          } else {
            child.height = item.height
            child.width = item.width
          }
        }
        
        if (item.orientation) {
          child.orient = `rotate-${item.orientation}`
        }
        
        if (!child.side) {
          child.side = parent$.side
        }
        
        if (child.children?.length) {
          this.mapChildren(child)
        }
      })
      
      return item
    },

    parseData(arr: DeviceChild[]): DeviceChild[] {
      arr.forEach(item => {
        if (item.children?.length) {
          this.mapChildren(item)
        }
      })
      return arr
    },

    getListeners(elem: DeviceChild): Record<string, (e: MouseEvent) => void> {
      const side$ = this.checkSide(elem)
      
      if (this.side === side$) {
        return {
          click: (e: MouseEvent) => {
            e.stopPropagation()
            this.deviceClickHandler(e)
          },
        }
      } else {
        return {
          click: (e: MouseEvent) => {
            e.stopPropagation()
          },
        }
      }
    },

    getClass(elem: DeviceChild): string[] | undefined {
      const side$ = this.checkSide(elem)
      if (this.side !== side$) return undefined
      
      const classList: string[] = []
      
      if (this.editable) {
        classList.push(elem.cls || '')
      }
      
      if (elem.orient) {
        classList.push(elem.orient)
      }
      
      if (elem.is_broken) {
        classList.push('broken')
      }
      
      if (elem.is_used) {
        classList.push('used')
      }
      
      return classList
    },

    getStyle(elem: DeviceChild): StyleObject {
      const obj: StyleObject = {
        width: `${elem.width}px`,
        height: `${elem.height}px`,
        position: elem.cls === 'module' ? 'fixed' : 'absolute',
      }
      
      if (elem.cls !== 'module') {
        obj.top = `${elem.y}px`
        obj.left = `${elem.x}px`
      }
      
      if (elem.cls === 'module' || elem.cls === 'submodule') {
        obj.backgroundImage = `url(${this.getBackground(elem)})`
        if (elem.slot_size == 1) {
          obj.backgroundSize = '100% 100%'
        }
        if (this.fit) {
          obj.backgroundSize = '100% 100%'
        }
      }
      
      return obj
    },

    getMount(data$: DeviceData): string | undefined {
      if (data$.inverse_mount_side === 'mount_rear') {
        return data$.mount === 'front' ? 'rear' : 'front'
      } else {
        return data$.mount
      }
    },
  },

  render(createElement: CreateElement): VNode | undefined {
    const getData = (arr: DeviceChild[]): VNode[] => {
      if (!arr.length) return []
      
      const vDom: VNode[] = []
      
      arr.forEach(elem => {
        if (elem.cls === 'port') return
        
        const tag = 'div'
        const options = {
          attrs: {
            id: `${elem.cls}$${elem.id}`,
          },
          class: this.getClass(elem),
          style: this.getStyle(elem),
        }
        
        let inner: VNode[] = []
        if (elem.children?.length) {
          const childInner = getData(elem.children)
          inner.push(...childInner)
        }
        
        const child = createElement(tag, options, inner)
        vDom.push(child)
      })
      
      return vDom
    }

    const vDom = getData(this.getModel)

    if (this.data) {
      const mountSide = this.getMount(this.data)
      const isCorrectSide = mountSide === this.side
      
      return createElement(
        'div',
        {
          class: isCorrectSide ? 'parent-class4' : 'parent-class2',
          attrs: {
            id: `${this.data.cls}$${this.data.id}`,
            title: `${this.data.template} ${this.data.name}`,
          },
          style: {
            width: this.fit ? `${this.x}px` : `${this.data.width}px`,
            height: this.fit ? `${this.y}px` : `${this.data.height}px`,
            top: '0px',
            left: '0px',
            backgroundImage: `url(${this.getBackground(this.data)})`,
            position: 'relative',
            backgroundSize: this.fit ? '100% 100%' : '',
            border: (this.getSelected && this.getSelected.$id === `${this.data.cls}$${this.data.id}`) ? 'solid 1px orange' : '',
          },
          on: {
            click: this.deviceClickHandler,
          },
        },
        vDom
      )
    }
    
    return undefined
  },
})
</script>

<style lang="css" scoped>


.parent-class4 {

  /* background-color: orange; */
  /* width: 450px;
  height: 355px; */
  /* transform: scale(.5, .5) */
  opacity: 1;
  z-index: 4;

}

.parent-class2 {

  /* background-color: orange; */
  /* width: 450px;
  height: 355px; */
  /* transform: scale(.5, .5) */
  opacity: 1;
  z-index: 2;

}

.parent-class2:after {

  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,.7);
  z-index: 3;

}

.slot :hover {

  background-color: #F7941D;

}

.subslot {

  /* background-color: black; */

}

.module {

  /* background-color: blue; */
  opacity: 1

}

  :hover {

    /* box-sizing: content-box;
    border: solid 1px #F7941D; */
    opacity: 1

  }

  .rotate-1 {

    transform-origin: 0 0;
    transform: rotate(90deg)translate(0, -100%);

  }
  
  .rotate-2 {

    transform-origin: 0 0;
    transform: rotate(180deg)translate(0, 0);

  }
  .rotate-3 {

    transform-origin: 0 0;
    transform: rotate(270deg)translate(-100%, 0);

  }

  .broken {

    background-color: red;

  }

.socket {

  /* background-color: purple; */

}

  .used {

    box-sizing: content-box;
    border: solid 1px #F7941D;
    opacity: 1;

  }

  /* :hover {

    background-color: #F7941D;

  } */




</style>
